import type { ReactNode } from "react";
import { Box, Stack, Text } from "@mantine/core";

type MantineTextSize = "xs" | "sm" | "md";

export type SarahHelpMarkdownProps = {
  content: string;
  color?: string;
  size?: MantineTextSize;
};

/** Inline: **bold**, *italic*, `code`, and plain email mailto links — no HTML from the model. */
function linkEmails(text: string, keyPrefix: string): ReactNode[] {
  const re = /\b([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})\b/gi;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const email = match[1]!;
    nodes.push(
      <Box
        component="a"
        key={`${keyPrefix}-em-${i++}`}
        href={`mailto:${email}`}
        style={{ color: "#0369A1", fontWeight: 600, textDecoration: "underline" }}
      >
        {email}
      </Box>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length ? nodes : [text];
}

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Bold before italic; support __bold__ and _italic_ as well
  const re = /\*\*([^*]+)\*\*|__([^_]+)__|`([^`]+)`|\*([^*\n]+)\*|_([^_\n]+)_/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  const pushPlain = (plain: string) => {
    nodes.push(...linkEmails(plain, `p-${key++}`));
  };

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushPlain(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined || match[2] !== undefined) {
      const bold = match[1] ?? match[2] ?? "";
      nodes.push(
        <Box component="strong" key={`b-${key++}`} style={{ fontWeight: 700 }}>
          {linkEmails(bold, `b-${key}`)}
        </Box>,
      );
    } else if (match[3] !== undefined) {
      nodes.push(
        <Box
          component="code"
          key={`c-${key++}`}
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: "0.92em",
            padding: "1px 4px",
            borderRadius: 4,
            background: "rgba(15, 23, 42, 0.06)",
          }}
        >
          {match[3]}
        </Box>,
      );
    } else if (match[4] !== undefined || match[5] !== undefined) {
      const em = match[4] ?? match[5] ?? "";
      nodes.push(
        <Box component="em" key={`i-${key++}`} style={{ fontStyle: "italic" }}>
          {em}
        </Box>,
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    pushPlain(text.slice(lastIndex));
  }

  return nodes.length ? nodes : [text];
}

/**
 * Lightweight markdown for Sarah help replies (headings, lists, bold, italics).
 * Does not parse HTML — safe for untrusted LLM / FAQ text.
 */
export function SarahHelpMarkdown({
  content,
  color = "#0F172A",
  size = "sm",
}: SarahHelpMarkdownProps) {
  const lines = String(content ?? "")
    .replace(/\r\n/g, "\n")
    .split("\n");

  const isCompact = size === "xs";
  const bodyStyle = {
    lineHeight: isCompact ? 1.4 : 1.55,
    ...(isCompact ? { fontSize: 12 } : {}),
  };

  return (
    <Stack gap={isCompact ? 2 : 4}>
      {lines.map((line, i) => {
        if (line.trim() === "") {
          return <Box key={`gap-${i}`} h={isCompact ? 4 : 6} />;
        }

        const heading = line.match(/^(#{1,3})\s+(.*)$/);
        if (heading) {
          const level = heading[1]!.length;
          const fontSize = isCompact
            ? size
            : level === 1
              ? "lg"
              : level === 2
                ? "md"
                : size;
          return (
            <Text
              key={`h-${i}`}
              size={fontSize as MantineTextSize}
              c={color}
              fw={700}
              component="div"
              style={{
                lineHeight: 1.3,
                marginTop: isCompact ? 2 : 4,
                ...(isCompact ? { fontSize: 12 } : {}),
              }}
            >
              {renderInline(heading[2] ?? "")}
            </Text>
          );
        }

        const ordered = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
        if (ordered) {
          const [, indent, num, rest] = ordered;
          const pad = Math.min((indent?.length ?? 0) * 4, 24);
          return (
            <Text
              key={`ol-${i}`}
              size={size}
              c={color}
              component="div"
              style={{
                ...bodyStyle,
                paddingLeft: pad,
                display: "flex",
                gap: 6,
              }}
            >
              <Box component="span" style={{ flexShrink: 0, minWidth: isCompact ? 14 : 18, fontWeight: 600 }}>
                {num}.
              </Box>
              <Box component="span" style={{ flex: 1 }}>
                {renderInline(rest ?? "")}
              </Box>
            </Text>
          );
        }

        const bullet = line.match(/^(\s*)[-*•]\s+(.*)$/);
        if (bullet) {
          const [, indent, rest] = bullet;
          const pad = Math.min((indent?.length ?? 0) * 4, 24);
          return (
            <Text
              key={`ul-${i}`}
              size={size}
              c={color}
              component="div"
              style={{
                ...bodyStyle,
                paddingLeft: pad,
                display: "flex",
                gap: 6,
              }}
            >
              <Box component="span" style={{ flexShrink: 0 }}>
                •
              </Box>
              <Box component="span" style={{ flex: 1 }}>
                {renderInline(rest ?? "")}
              </Box>
            </Text>
          );
        }

        return (
          <Text key={`p-${i}`} size={size} c={color} component="div" style={bodyStyle}>
            {renderInline(line)}
          </Text>
        );
      })}
    </Stack>
  );
}
